import React, { useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import toast from "react-hot-toast";

/** Try to pluck a usable URL string out of various API shapes */
function normalizeVideo(v: unknown): string | null {
  if (!v) return null;
  if (typeof v === "string") return v;
  // common shapes: { url: "..."} or { path: "..." }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (typeof o.url === "string") return o.url;
    if (typeof o.path === "string") return o.path;
  }
  return null;
}

/** Derive server origin from axios baseURL (e.g. http://localhost:3000/api -> http://localhost:3000) */
function getServerOrigin(): string {
  const base: string = (axios.defaults as any)?.baseURL ?? "";
  if (!base) return "http://localhost:3000";
  // strip trailing "/api" (with or without trailing slash)
  const m = base.match(/^(.*?)(\/api\/?|)$/i);
  return m?.[1] || base || "http://localhost:3000";
}

const AboutPage: React.FC = () => {
  const [section1, setSection1] = useState("");
  const [section2, setSection2] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Load about content on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/content/about");
        const data = res.data?.data ?? {};
        setSection1(typeof data.section1 === "string" ? data.section1 : "");
        setSection2(typeof data.section2 === "string" ? data.section2 : "");
        const normalized = normalizeVideo(data.video);
        setPreviewUrl(normalized);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load About content");
      }
    })();
  }, []);

  // Clean up object URLs if user picks files repeatedly
  useEffect(() => {
    return () => {
      // Revoke on unmount (in case previewUrl is a blob: link we created)
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setVideoFile(file);
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setPreviewUrl((old) => {
        if (old?.startsWith("blob:")) URL.revokeObjectURL(old);
        return blobUrl;
      });
    }
  };

  const serverOrigin = useMemo(getServerOrigin, []);
  const resolvedVideoSrc = useMemo(() => {
    if (!previewUrl) return null;
    // If server saved a relative path like "/uploads/xyz.mp4" -> prefix with server origin
    if (previewUrl.startsWith("/")) return `${serverOrigin}${previewUrl}`;
    // Already absolute (http, https, blob, data, etc)
    return previewUrl;
  }, [previewUrl, serverOrigin]);

  const handleSubmit = async () => {
    if (!section1.trim() || !section2.trim()) {
      return toast.error("Please fill both sections.");
    }

    // You only must upload a video if there isn't one already
    const mustUploadVideo = !previewUrl && !videoFile;
    if (mustUploadVideo) {
      return toast.error("Please upload the intro video.");
    }

    const formData = new FormData();
    formData.append("section1", section1);
    formData.append("section2", section2);
    if (videoFile) {
      formData.append("video", videoFile);
    }

    try {
      setLoading(true);
      await axios.put("/content/about", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("About page updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update About page");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl p-6 mx-auto bg-white rounded shadow">
      <h2 className="mb-6 text-2xl font-bold text-blue-900">Edit About Page</h2>

      {/* Section 1 */}
      <div className="mb-4">
        <label className="block mb-1 text-sm font-semibold text-blue-900">
          Section 1 Content
        </label>
        <textarea
          value={section1}
          onChange={(e) => setSection1(e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded"
          placeholder="Type content for the first section..."
        />
      </div>

      {/* Section 2 */}
      <div className="mb-4">
        <label className="block mb-1 text-sm font-semibold text-blue-900">
          Section 2 Content
        </label>
        <textarea
          value={section2}
          onChange={(e) => setSection2(e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded"
          placeholder="Type content for the second section..."
        />
      </div>

      {/* Video Upload */}
      <div className="mb-6">
        <label className="block mb-1 text-sm font-semibold text-blue-900">
          Upload Intro Video
        </label>
        <input
          type="file"
          accept="video/*"
          onChange={handleVideoChange}
          className="block w-full"
        />

        {resolvedVideoSrc && (
          <video
            src={resolvedVideoSrc}
            className="w-full mt-4 rounded shadow"
            controls
            height={240}
          />
        )}
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="px-6 py-2 font-semibold text-blue-900 transition bg-yellow-400 rounded hover:bg-yellow-300 disabled:opacity-60"
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
};

export default AboutPage;
